import { useEffect, useMemo, useState } from "react"
import { useFetch } from "src/shared/hooks/useFetch"
import { useProfile } from "src/shared/hooks/useProfile"
import { HistorialVentasArticulos, TablaVentasArticulo } from "./ArticulosVendidos.type"
import FlexibleTable, {
    SortFilterValue,
    TableFilter,
} from "src/shared/components/data-display/FlexibleTable/FlexibleTable"
import { tableSkeletonRows } from "src/shared/components/layout/skeleton/TableSkeleton/TableSkeleton"
import "./ArticulosVendidos.css"
import TablePaginatorWrapper from "src/shared/components/data-display/FlexibleTable/sections/TablePaginatorWrapper/TablePaginatorWrapper"
import TableWrappper from "src/pages/reservaciones/inicio/sections/TableWrapper/TableWrapper"
import {
    FlexibleTableHeaderColumn,
    FlexibleTableRow,
} from "src/shared/components/data-display/FlexibleTable/flexible-table-items.interface"
import "../../Reports.css"
import FloatButon from "src/shared/components/layout/FloatButon/FloatButon"
import useDownloadPdf from "src/shared/hooks/useDownloadPdf"
import CardReports from "../../components/card-reports/CardReports"
import { formatCurrencyToFixed } from "src/shared/hooks/formatCurrency"
import ReportWrapper from "../../components/report-wrapper/ReportWrapper"
import ReportError from "../../components/report-error/ReportError"
import useArticuloHeader from "../hooks/useArticuloHeader"
import useOrigenHeader from "../hooks/useOrigenHeader"
import useCategoriasArticulosHeader from "../hooks/useCategoriasArticulosHeader"
import useTurnoHeader from "../hooks/useTurnoHeader"
import useFormattedMeta from "../hooks/useFormattedMeta"
import useDedupedRefetch from "../hooks/useDedupedRefetch"
import { Stringify } from "src/shared/types/Stringify"
import reportSelectedHandler from "../../helpers/reportSelectedHandler"
import { ReportsOptions } from "../../Reports.type"
import ReportLoaderModal from "../../components/report-loader/ReportLoaderModal"
import MontoVenta from "./sections/MontoVenta/MontoVenta"
import ConsumoTurnos from "./sections/ConsumoTurnos/ConsumoTurnos"
import ResumenCategoria from "./sections/ResumenCategoria/ResumenCategoria"
import TopArticulos from "./sections/TopArticulos/TopArticulos"
import useColaboradoresHeader from "../hooks/useColaboradoresHeader"
import useTipoVentaHeader from "../hooks/useTipoVentaHeader"
import useMetodoPagoHeader from "../hooks/useMetodoPagoHeader"
import { splitBySlash } from "src/shared/helpers/splitBySlash"
import { formatTipoPago } from "src/shared/helpers/formatTipoPago"

const ARTICULO_HEADER_VALUE = "Articulo"
const RESPONSABLE_HEADER_VALUE = "Responsable"
const COLABORADOR_HEADER_VALUE = "Colaborador"
const ORIGEN_HEADER_VALUE = "Origen"
const CATEGORIA_HEADER_VALUE = "Categoría"
const TURNO_HEADER_VALUE = "Turno"
const TIPO_VENTA_HEADER_VALUE = "TipoVenta"

const FECHA_HEADER_VALUE = "Fecha"
const TICKET_HEADER_VALUE = "Ticket"

const ArticulosVendidos = ({ apiDateFilter }: { apiDateFilter: string[] | null }) => {
    const { hotel_id } = useProfile()

    const { data, refetch, load } = useFetch<HistorialVentasArticulos>("/reportes/tabla_venta_articulos", {
        startFetch: false,
    })

    const [currentPage, setcurrentPage] = useState(1)
    const { download } = useDownloadPdf()
    const articuloHeader = useArticuloHeader<Stringify<TablaVentasArticulo>>({
        headerValue: ARTICULO_HEADER_VALUE,
        valueToDisplay: "Artículo",
    })
    const responsableHeader = useColaboradoresHeader<Stringify<TablaVentasArticulo>>({
        headerValue: "Responsable",
        valueToDisplay: RESPONSABLE_HEADER_VALUE,
    })
    const colaboradorHeader = useColaboradoresHeader<Stringify<TablaVentasArticulo>>({
        headerValue: "Colaborador",
        valueToDisplay: COLABORADOR_HEADER_VALUE,
    })
    const origenHeader = useOrigenHeader<Stringify<TablaVentasArticulo>>({
        headerValue: "Origen",
        valueToDisplay: ORIGEN_HEADER_VALUE,
    })
    const categoriasArticulosHeader = useCategoriasArticulosHeader<Stringify<TablaVentasArticulo>>({
        headerValue: "Categoria",
        valueToDisplay: CATEGORIA_HEADER_VALUE,
    })
    const turnosHeader = useTurnoHeader<Stringify<TablaVentasArticulo>>({
        headerValue: "Turno",
        valueToDisplay: TURNO_HEADER_VALUE,
    })

    const tipoVentaHeader = useTipoVentaHeader<Stringify<TablaVentasArticulo>>({
        headerValue: "TipoVenta",
        valueToDisplay: "Tipo de venta",
    })
    const metodoPagoHeader = useMetodoPagoHeader<Stringify<TablaVentasArticulo>>({
        headerValue: "MetodoPago",
        valueToDisplay: "Método de pago",
    })

    const [sortValue, setsortValue] = useState<SortFilterValue | null>(null)
    const [filterValues, setfilterValues] = useState<TableFilter[] | null>(null)

    const formattedMetaValues = useMemo<{
        filterValues: TableFilter[] | null
        sortValue: SortFilterValue | null
        headerValues: Stringify<TablaVentasArticulo>[]
    }>(
        () => ({
            filterValues,
            sortValue,
            headerValues: ["Articulo", "Responsable", "Origen", "Categoria", "Turno", "Fecha", "Ticket", "TipoVenta", "Colaborador", "MetodoPago"],
        }),
        [filterValues, sortValue]
    )

    const { formattedMeta } = useFormattedMeta(formattedMetaValues)

    const { error: fetchError, retry } = useDedupedRefetch(
        refetch,
        apiDateFilter?.length
            ? {
                hotel_id,
                fecha_inicio: apiDateFilter[0],
                fecha_fin: apiDateFilter[1],
                take: 15,
                page: currentPage,
                ...(formattedMeta?.filtro ? { filtro: JSON.stringify(formattedMeta.filtro) } : {}),
                ...(formattedMeta?.orden ? { orden: JSON.stringify(formattedMeta.orden) } : {}),
            }
            : null
    )

    const headers: FlexibleTableHeaderColumn[] = [
        {
            value: TICKET_HEADER_VALUE,
            sort: true,
        },
        { value: FECHA_HEADER_VALUE, sort: true },
        turnosHeader,
        origenHeader,
        responsableHeader,
        articuloHeader,
        categoriasArticulosHeader,
        tipoVentaHeader,
        { value: "Precio" },
        { value: "Cantidad" },
        metodoPagoHeader,
        colaboradorHeader,
        { value: "Total" }
    ]

    const { skeletonRows } = tableSkeletonRows({ headers })

    const tableItems: FlexibleTableRow[] = data?.respuesta?.tabla_ventas_articulos?.map((v, index) => ({
        value: [
            { value: v.Ticket, className: "reports-cell" },
            { value: v.Fecha, className: "reports-cell" },
            { value: v.Turno, className: "reports-cell", fromHeaderFilter: TURNO_HEADER_VALUE },
            {
                value: (() => {
                    const origins = splitBySlash(v.Origen || "")
                    return (
                        <div style={{ display: "flex", flexDirection: "column" }}>
                            <span>{origins.length > 1 ? "Mixto" : origins[0]?.[0] || "N/A"}</span>
                            {(origins.length > 1 || !!origins[0]?.[1]) && (
                                <div style={{ display: "flex", flexWrap: "wrap" }}>
                                    {origins.map((o, index) => (
                                        <span key={index} style={{ color: "var(--placeholder)" }}>
                                            {o[1] ?? o[0]}
                                            {index < origins.length - 1 ? ", " : ""}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    )
                })(),
                className: "reports-cell",
                fromHeaderFilter: ORIGEN_HEADER_VALUE,
            },
            { value: v.Responsable, className: "reports-cell", fromHeaderFilter: RESPONSABLE_HEADER_VALUE },
            { value: v.Articulo, className: "reports-cell", fromHeaderFilter: ARTICULO_HEADER_VALUE },
            { value: v.Categoria, className: "reports-cell", fromHeaderFilter: CATEGORIA_HEADER_VALUE },
            { value: v.TipoVenta, className: "reports-cell", fromHeaderFilter: TIPO_VENTA_HEADER_VALUE },
            { value: formatCurrencyToFixed(Number(v.Precio)), className: "reports-cell" },
            { value: v.Cantidad, className: "reports-cell" },
            {
                value: (() => {
                    const payments = splitBySlash(v.MetodoPago || "")
                    return (
                        <div style={{ display: "flex", flexDirection: "column" }}>
                            <span>{payments.length > 1 ? "Mixto" : formatTipoPago(payments[0]?.[0]) || "N/A"}</span>
                            {(payments.length > 1 || !!payments[0]?.[1]) && (
                                <div style={{ display: "flex", flexWrap: "wrap" }}>
                                    {payments.map((p, index) => (
                                        <span key={index} style={{ color: "var(--placeholder)" }}>
                                            {p[1] ?? p[0]}
                                            {index < payments.length - 1 ? ", " : ""}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    )
                })(),
                className: "reports-cell",
            },
            { value: v.Colaborador, className: "reports-cell", fromHeaderFilter: COLABORADOR_HEADER_VALUE },
            { value: formatCurrencyToFixed(Number(v.Total)), className: "reports-cell" },
        ],
        key: `row-${index}`,
    }))

    const { setSelectedSection } = reportSelectedHandler()

    useEffect(() => {
        setSelectedSection(ReportsOptions.artVendidos)
    }, [])

    useEffect(() => {
        setcurrentPage(1)
    }, [apiDateFilter])


    if (fetchError) {
        return <ReportError onRetry={retry} />
    }

    return (
        <>
            {load &&
                <ReportLoaderModal isOpen />
            }
            <ReportWrapper height="calc(100dvh - 125px)">
                <CardReports height="200px" >
                    <MontoVenta totalVentas={data?.dashboard?.total_ventas} />
                    <ConsumoTurnos
                        porcentajeMatutino={data?.dashboard?.porcentaje_ventas_por_turno?.Matutino || 0}
                        porcentajeVespertino={data?.dashboard?.porcentaje_ventas_por_turno?.Vespertino || 0}
                        porcentajeNocturno={data?.dashboard?.porcentaje_ventas_por_turno?.Nocturno || 0}
                    />
                    <ResumenCategoria ventas={data?.dashboard?.ventas_por_categoria} />
                    <TopArticulos articulos={data?.dashboard?.top_5_articulos || []} />
                </CardReports>
                <TablePaginatorWrapper
                    currentPage={currentPage}
                    onChange={(p) => setcurrentPage(p)}
                    pages={data?.respuesta?.total_paginas || 0}
                    className="articulos-vendidos__table__wrapper"
                    style={{
                        justifyContent: data?.respuesta?.tabla_ventas_articulos?.length ? "flex-start" : "center",
                        borderRadius: "0 0 20px 0",
                    }}
                >
                    <TableWrappper isThisComponentInsideAPaginatorWrapper={!!data?.respuesta?.tabla_ventas_articulos?.length}>
                        <FlexibleTable
                            onSelectedFilters={(v) => {
                                setcurrentPage(1)
                                setfilterValues(v)
                            }}
                            containerStyle={{
                                ...(load ? { overflow: "hidden" } : {})
                            }}
                            onSort={(v) => {
                                setcurrentPage(1)
                                setsortValue(v)
                            }}
                            emptyState={{
                                headerIcon: "PieChartFilled",
                                titile: "Aún no hay reportes disponibles",
                                subTitle:
                                    "Todavía no hay datos para mostrar en esta sección. Una vez que se genere un reporte, lo verás aquí.",
                            }}
                            containterClassName="articulos-vendidos__table"
                            tableItems={{
                                headers,
                                rows: load ? skeletonRows : tableItems,
                            }}
                        />
                    </TableWrappper>
                    <FloatButon
                        icon="Download"
                        onAdd={() => {
                            if (!apiDateFilter?.length) {
                                return
                            }
                            download({
                                endpoint: "/reportes/tabla_venta_articulos",
                                params: {
                                    hotel_id,
                                    fecha_inicio: apiDateFilter[0],
                                    fecha_fin: apiDateFilter[1],
                                    descargar: true,
                                    ...(formattedMeta?.filtro ? { filtro: JSON.stringify(formattedMeta.filtro) } : {}),
                                    ...(formattedMeta?.orden ? { orden: JSON.stringify(formattedMeta.orden) } : {})
                                },
                                title: "articulos_vendidos",
                            })
                        }}
                    />
                </TablePaginatorWrapper>
            </ReportWrapper>
        </>
    )
}

export default ArticulosVendidos

